/**
 * Canvas → MediaStream that keeps emitting frames while the Twinnee tab is hidden.
 *
 * captureStream(30) often stalls in background tabs (tied to the page's rAF budget),
 * which freezes pitch recordings as a still (background image) or black frames.
 * Manual captureStream(0) + requestFrame() after each paint stays live with our worker frame loop.
 */

/**
 * @param {HTMLCanvasElement} canvas
 * @returns {{ stream: MediaStream, pushFrame: () => void, stop: () => void }}
 */
export function createCanvasCaptureStream(canvas) {
  if (!canvas || typeof canvas.captureStream !== 'function') {
    throw new Error('This browser cannot record canvas video');
  }

  let stream;
  let track = null;
  let manual = false;

  try {
    stream = canvas.captureStream(0);
    track = stream.getVideoTracks?.()[0] || null;
    manual = Boolean(track && typeof track.requestFrame === 'function');
    if (!manual) {
      stream.getTracks?.().forEach((t) => t.stop());
      stream = canvas.captureStream(30);
      track = stream.getVideoTracks?.()[0] || null;
    }
  } catch {
    stream = canvas.captureStream(30);
    track = stream.getVideoTracks?.()[0] || null;
    manual = Boolean(track && typeof track.requestFrame === 'function');
  }

  const pushFrame = () => {
    if (!manual || !track || track.readyState !== 'live') return;
    try {
      track.requestFrame();
    } catch {
      /* ignore */
    }
  };

  const stop = () => {
    try {
      stream?.getTracks?.().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    track = null;
  };

  return { stream, pushFrame, stop };
}
